import { LineChartBarSquare } from '@qr-front/icons/line-chart-bar-square'
import { LineCheckCircle } from '@qr-front/icons/line-check-circle'
import { LineCopy } from '@qr-front/icons/line-copy'
import { LineDownload } from '@qr-front/icons/line-download'
import { LineEdit } from '@qr-front/icons/line-edit'
import { LineLock } from '@qr-front/icons/line-lock'
import { LinePauseCircle } from '@qr-front/icons/line-pause-circle'
import { LineQrType } from '@qr-front/icons/line-qr-type'
import { LineRefreshCw } from '@qr-front/icons/line-refresh-cw'
import { LineShareAndroid } from '@qr-front/icons/line-share-android'
import { LineTrashAlt } from '@qr-front/icons/line-trash-alt'
import { getApiClient } from 'api/apiClient'
import { API_ROUTES } from 'constants/apiRoutesConstants'
import { ROUTES } from 'constants/routesConstants'
import { ReactElement } from 'react'
import { DOWNLOAD_QR_ACTION_TYPES, downloadQRCode } from 'utils/downloadQrCode'

import { QR_TYPE, qrCodesApi, TQrData } from '@/generator/shared'
import { TRouter } from '@/i18n-routing'
import { getStore } from '@/store'

// Not the generator barrel — that closes a circular import (TDZ crash on dev SSR)
import { buildEditModeContentFormPageUrl } from '../../../../../../generator/utils/BuildEditModeContentFormPageUrl'
import {
	confirmationDialogTypeState,
	disableQrPasswordRequestAtom,
	enableQrPasswordDialogAtom,
	qrItemState,
} from '../../dialogsState'
import { duplicatedQrHighlightAtom, qrListAtom } from '../../tableState'
import { withExpiredDialogIfNotAllowed } from './tableActionUtils'

export const enum TABLE_ROW_ACTION_TYPE {
	More = 'more',
	Download = 'download',
}

export const enum TABLE_ACTION_TYPE {
	Edit = 'edit',
	Analytics = 'analytics',
	Pause = 'pause',
	Activate = 'activate',
	Duplicate = 'duplicate',
	ResetScans = 'resetScans',
	ChangeQrType = 'changeQrType',
	EnablePassword = 'enablePassword',
	DisablePassword = 'disablePassword',
	Share = 'share',
	Download = 'download',
	DownloadAsSvg = 'downloadAsSvg',
	DownloadAsPng = 'downloadAsPng',
	DownloadAsJpg = 'downloadAsJpg',
	Delete = 'delete',
	BulkPause = 'bulkPause',
	BulkActivate = 'bulkActivate',
	BulkResetScans = 'bulkResetScans',
	BulkDelete = 'bulkDelete',
}

export type TAction = (qrItem: TQrData) => void

export type TActionOption = {
	key: TABLE_ACTION_TYPE
	translationKey: string
	action: TAction
	Icon?: ReactElement
}

const moreActions = ({
	isMobile = false,
	router,
	qrItem,
}: {
	isMobile: boolean
	router: TRouter
	qrItem: TQrData
}): TActionOption[] => {
	const pauseActivateOption: TActionOption = qrItem?.disabled
		? {
				key: TABLE_ACTION_TYPE.Activate,
				translationKey:
					'dashboard__qr_table__qr_card__download_option__activate',
				Icon: <LineCheckCircle />,
				action: withExpiredDialogIfNotAllowed((qrItem: TQrData) => {
					getStore().set(qrItemState, qrItem)
					getStore().set(
						confirmationDialogTypeState,
						TABLE_ACTION_TYPE.Activate,
					)
				}),
			}
		: {
				key: TABLE_ACTION_TYPE.Pause,
				translationKey:
					'dashboard__qr_table__qr_card__download_option__pause',
				Icon: <LinePauseCircle />,
				action: (qrItem) => {
					getStore().set(qrItemState, qrItem)
					getStore().set(
						confirmationDialogTypeState,
						TABLE_ACTION_TYPE.Pause,
					)
				},
			}

	const isWifi = qrItem?.content?.type === QR_TYPE.Wifi
	const passwordOption: TActionOption = qrItem?.hasPassword
		? {
				key: TABLE_ACTION_TYPE.DisablePassword,
				translationKey:
					'dashboard__qr_table__qr_card__download_option__disable_password',
				Icon: <LineLock />,
				action: withExpiredDialogIfNotAllowed((qrItem: TQrData) => {
					getStore().set(disableQrPasswordRequestAtom, qrItem)
				}),
			}
		: {
				key: TABLE_ACTION_TYPE.EnablePassword,
				translationKey:
					'dashboard__qr_table__qr_card__download_option__enable_password',
				Icon: <LineLock />,
				action: withExpiredDialogIfNotAllowed((qrItem: TQrData) => {
					getStore().set(qrItemState, qrItem)
					getStore().set(enableQrPasswordDialogAtom, true)
				}),
			}

	const desktopOptions: TActionOption[] = [
		{
			key: TABLE_ACTION_TYPE.Edit,
			translationKey:
				'dashboard__qr_table__qr_card__download_option__edit',
			action: withExpiredDialogIfNotAllowed(async (qrItem: TQrData) => {
				try {
					const {
						data: { id: tempQrId },
					} = await qrCodesApi.postQrPreview(qrItem.id)
					router.push(
						buildEditModeContentFormPageUrl({
							qrType: qrItem.content.type,
							targetId: qrItem.id,
							tempQrId,
							isChangeTypeMode: false,
						}),
					)
				} catch (error) {
					console.error('Failed opening QR editor', error)
				}
			}),
			Icon: <LineEdit />,
		},
		{
			key: TABLE_ACTION_TYPE.Analytics,
			translationKey:
				'dashboard__qr_table__qr_card__download_option__analytics',
			action: withExpiredDialogIfNotAllowed((qrItem: TQrData) => {
				router.push(`${ROUTES.cabinet.analytics}?id=${qrItem.id}`)
			}),
			Icon: <LineChartBarSquare />,
		},
		pauseActivateOption,
		{
			key: TABLE_ACTION_TYPE.Duplicate,
			translationKey:
				'dashboard__qr_table__qr_card__download_option__duplicate',
			action: withExpiredDialogIfNotAllowed(async (qrItem: TQrData) => {
				if (!qrItem?.id) return
				try {
					await getApiClient().post(
						`${API_ROUTES.qrCode.duplicate}/${qrItem.id}`,
					)
					const response = await getApiClient().get(
						API_ROUTES.qrCode.base,
					)
					const qrCodes = Array.isArray(response.data)
						? response.data
						: []
					getStore().set(qrListAtom, qrCodes)

					if (qrCodes?.[0]?.id) {
						getStore().set(
							duplicatedQrHighlightAtom,
							qrCodes?.[0].id,
						)

						setTimeout(() => {
							getStore().set(duplicatedQrHighlightAtom, null)
						}, 3000)
					}
				} catch (error) {
					console.error(error)
				}
			}),
			Icon: <LineCopy />,
		},
		{
			key: TABLE_ACTION_TYPE.ResetScans,
			translationKey:
				'dashboard__qr_table__qr_card__download_option__reset_scans',
			action: withExpiredDialogIfNotAllowed((qrItem: TQrData) => {
				getStore().set(qrItemState, qrItem)
				getStore().set(
					confirmationDialogTypeState,
					TABLE_ACTION_TYPE.ResetScans,
				)
			}),
			Icon: <LineRefreshCw />,
		},
		{
			key: TABLE_ACTION_TYPE.ChangeQrType,
			translationKey:
				'dashboard__qr_table__qr_card__download_option__change_qr_type',
			action: withExpiredDialogIfNotAllowed((qrItem: TQrData) => {
				getStore().set(qrItemState, qrItem)
				getStore().set(
					confirmationDialogTypeState,
					TABLE_ACTION_TYPE.ChangeQrType,
				)
			}),
			Icon: <LineQrType />,
		},
		...(isWifi ? [] : [passwordOption]),
	]

	const mobileExtraOptions: TActionOption[] = isMobile
		? [
				{
					key: TABLE_ACTION_TYPE.Share,
					translationKey:
						'dashboard__qr_table__qr_card__download_option__share',
					action: () => {},
					Icon: <LineShareAndroid />,
				},
				{
					key: TABLE_ACTION_TYPE.Download,
					translationKey:
						'dashboard__qr_table__qr_card__download_option__download',
					action: () => {},
					Icon: <LineDownload />,
				},
			]
		: []

	const deleteOption: TActionOption = {
		key: TABLE_ACTION_TYPE.Delete,
		translationKey: 'dashboard__qr_table__qr_card__download_option__delete',
		Icon: <LineTrashAlt className="text-error" />,
		action: (qrItem) => {
			getStore().set(qrItemState, qrItem)
			getStore().set(
				confirmationDialogTypeState,
				TABLE_ACTION_TYPE.Delete,
			)
		},
	}

	return [...desktopOptions, ...mobileExtraOptions, deleteOption]
}

export const moreActionsMobile = ({
	router,
	qrItem,
}: {
	router: TRouter
	qrItem: TQrData
}) => moreActions({ isMobile: true, router, qrItem })

export const moreActionsDesktop = ({
	router,
	qrItem,
}: {
	router: TRouter
	qrItem: TQrData
}) => moreActions({ isMobile: false, router, qrItem })

const handleDownloadAction = async (
	qrItem: TQrData,
	type: DOWNLOAD_QR_ACTION_TYPES,
) => {
	try {
		await downloadQRCode(qrItem, type)
	} catch (error) {
		console.error('Failed downloading QR code', error)
	}
}

export const downloadActions: TActionOption[] = [
	{
		key: TABLE_ACTION_TYPE.DownloadAsSvg,
		translationKey: 'dashboard__qr_table__qr_card__download_option__svg',
		action: (qrItem: TQrData) =>
			handleDownloadAction(qrItem, TABLE_ACTION_TYPE.DownloadAsSvg),
	},
	{
		key: TABLE_ACTION_TYPE.DownloadAsPng,
		translationKey: 'dashboard__qr_table__qr_card__download_option__png',
		action: (qrItem) =>
			handleDownloadAction(qrItem, TABLE_ACTION_TYPE.DownloadAsPng),
	},
	{
		key: TABLE_ACTION_TYPE.DownloadAsJpg,
		translationKey: 'dashboard__qr_table__qr_card__download_option__jpg',
		action: (qrItem) =>
			handleDownloadAction(qrItem, TABLE_ACTION_TYPE.DownloadAsJpg),
	},
]
